/**
 * CodeExecutor - Main code execution router
 * 
 * Routes code execution to the appropriate executor based on language and config.
 * - Python/JavaScript: Local execution (fast, instant feedback)
 * - Java/C++/C: Remote execution via PHP API
 */

import PythonExecutor from './executors/PythonExecutor';
import JavaScriptExecutor from './executors/JavaScriptExecutor';
import RemoteExecutor from './executors/RemoteExecutor';
import { configService } from './ConfigService';

class CodeExecutor {
  constructor() {
    this.executionConfig = null;
  }

  /**
   * Initialize the executor with config
   */
  initialize() {
    this.executionConfig = configService.getExecutionConfig();
  }

  /**
   * Execute code based on the configured language and mode
   * 
   * @param {string} code - The code to execute
   * @param {string} stdin - Optional input
   * @returns {Promise<{success: boolean, output: string, error: string|null, executionTime: number}>}
   */
  async execute(code, stdin = '') {
    if (!this.executionConfig) {
      this.initialize();
    }

    const { mode, engine, apiEndpoint, timeout } = this.executionConfig;

    if (mode === 'local') {
      return this.executeLocal(engine, code, stdin, timeout);
    } else {
      return this.executeRemote(engine, code, stdin, apiEndpoint, timeout);
    }
  }

  /**
   * Execute code locally (Python/JavaScript)
   */
  async executeLocal(engine, code, stdin, timeout) {
    switch (engine) {
      case 'skulpt':
        // Ensure Skulpt is loaded
        if (!PythonExecutor.isReady()) {
          try {
            await PythonExecutor.loadSkulpt();
          } catch (err) {
            return {
              success: false,
              output: '',
              error: 'Failed to load Python interpreter. Please refresh the page.',
              executionTime: 0
            };
          }
        }
        return PythonExecutor.execute(code, stdin, timeout);

      case 'javascript':
        return JavaScriptExecutor.execute(code, stdin, timeout);

      default:
        return {
          success: false,
          output: '',
          error: `Unknown local execution engine: ${engine}`,
          executionTime: 0
        };
    }
  }

  /**
   * Execute code remotely (Java/C++/C)
   */
  async executeRemote(engine, code, stdin, apiEndpoint, timeout) {
    if (!apiEndpoint) {
      return {
        success: false,
        output: '',
        error: 'Remote execution API not configured',
        executionTime: 0
      };
    }

    if (!RemoteExecutor.isReady()) {
      return {
        success: false,
        output: '',
        error: 'No internet connection. Remote execution requires online access.',
        executionTime: 0
      };
    }

    return RemoteExecutor.execute(apiEndpoint, engine, code, stdin, timeout);
  }

  /**
   * Execute code for a specific language (override config)
   */
  async executeForLanguage(language, code, stdin = '', options = {}) {
    const timeout = options.timeout || 5000;

    switch (language.toLowerCase()) {
      case 'python':
        if (!PythonExecutor.isReady()) {
          await PythonExecutor.loadSkulpt();
        }
        return PythonExecutor.execute(code, stdin, timeout);

      case 'javascript':
      case 'js':
        return JavaScriptExecutor.execute(code, stdin, timeout);

      case 'java':
      case 'cpp':
      case 'c++':
      case 'c':
        const apiEndpoint = options.apiEndpoint || this.executionConfig?.apiEndpoint;
        if (!apiEndpoint) {
          return {
            success: false,
            output: '',
            error: `Remote API required for ${language}. API not configured.`,
            executionTime: 0
          };
        }
        const lang = language.toLowerCase() === 'c++' ? 'cpp' : language.toLowerCase();
        return RemoteExecutor.execute(apiEndpoint, lang, code, stdin, timeout);

      default:
        return {
          success: false,
          output: '',
          error: `Unsupported language: ${language}`,
          executionTime: 0
        };
    }
  }

  /**
   * Validate code before execution
   */
  validateCode(code, maxLength = 10000) {
    if (!code || typeof code !== 'string') {
      return { valid: false, error: 'No code provided' };
    }

    if (code.length > maxLength) {
      return { valid: false, error: `Code exceeds maximum length of ${maxLength} characters` };
    }

    // Check for potentially problematic patterns
    const dangerousPatterns = [
      /import\s+os/i,
      /import\s+sys/i,
      /exec\s*\(/i,
      /eval\s*\(/i,
      /__import__/i,
      /open\s*\(/i,
      /subprocess/i,
      /system\s*\(/i
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(code)) {
        // For learning purposes, we'll allow these but log a warning
        console.warn('Code contains potentially restricted pattern:', pattern);
      }
    }

    return { valid: true };
  }

  /**
   * Run test cases against code
   */
  async runTestCases(code, testCases, language = null) {
    const results = [];
    const langConfig = language || this.executionConfig?.engine || 'python';

    for (const testCase of testCases) {
      const { input, expectedOutput, name } = testCase;
      
      let result;
      if (language) {
        result = await this.executeForLanguage(language, code, input);
      } else {
        result = await this.execute(code, input);
      }

      const actualOutput = result.output.trim();
      const expected = expectedOutput.trim();
      const passed = actualOutput === expected;

      results.push({
        name: name || `Test ${results.length + 1}`,
        passed,
        input,
        expectedOutput: expected,
        actualOutput,
        error: result.error,
        executionTime: result.executionTime
      });
    }

    return {
      totalTests: results.length,
      passed: results.filter(r => r.passed).length,
      failed: results.filter(r => !r.passed).length,
      results
    };
  }

  /**
   * Check if the executor is ready
   */
  isReady() {
    if (!this.executionConfig) {
      this.initialize();
    }

    const { mode, engine } = this.executionConfig;

    if (mode === 'local') {
      switch (engine) {
        case 'skulpt':
          return PythonExecutor.isReady();
        case 'javascript':
          return JavaScriptExecutor.isReady();
        default:
          return false;
      }
    } else {
      return RemoteExecutor.isReady();
    }
  }

  /**
   * Get information about the executor
   */
  getExecutorInfo() {
    if (!this.executionConfig) {
      this.initialize();
    }

    return {
      mode: this.executionConfig?.mode || 'unknown',
      engine: this.executionConfig?.engine || 'unknown',
      timeout: this.executionConfig?.timeout || 5000,
      isReady: this.isReady()
    };
  }
}

// Export singleton instance
export const codeExecutor = new CodeExecutor();
export default codeExecutor;